import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Shield,
  Lock,
  Eye,
  Database,
  FileText,
  Mail,
} from "lucide-react";
import { Button } from "./ui/button";
import { appData } from "../mock";
import "./PrivacyPolicy.css";

export const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="privacy-page">
      {/* Navigation Header */}
      <header className="privacy-nav-header">
        <div className="nav-content">
          <div className="nav-logo">
            <img
              src={appData.logoUrl}
              alt="Status Saver Logo"
              className="logo-img"
            />
            <span className="logo-text">{appData.name}</span>
          </div>
          <Button onClick={() => navigate("/")} className="btn-secondary">
            <ArrowLeft className="btn-icon" />
            Home
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="privacy-hero">
        <div className="privacy-hero-content">
          <Shield className="privacy-hero-icon" size={64} />
          <h1 className="privacy-hero-title">Privacy Policy</h1>
          <p className="privacy-hero-subtitle">Your privacy matters to us</p>
          <p className="privacy-last-updated">Last Updated: January 6, 2025</p>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="privacy-content">
        <div className="privacy-container">
          {/* Introduction */}
          <div className="privacy-section">
            <div className="section-icon-wrapper">
              <FileText className="section-icon" size={24} />
            </div>
            <h2 className="privacy-heading">Introduction</h2>
            <p className="privacy-text">
              Welcome to Status Saver. We respect your privacy and are committed
              to protecting your personal data. This privacy policy explains how
              we handle your information when you use our WhatsApp status saver
              application.
            </p>
            <p className="privacy-text">
              Status Saver is designed to save WhatsApp statuses locally on your
              device. We believe in transparency and want you to understand what
              data we collect (if any) and how we use it.
            </p>
          </div>

          {/* Information We Collect */}
          <div className="privacy-section">
            <div className="section-icon-wrapper">
              <Database className="section-icon" size={24} />
            </div>
            <h2 className="privacy-heading">Information We Collect</h2>
            <p className="privacy-text">
              Status Saver is designed with privacy in mind. Here's what you
              need to know:
            </p>
            <div className="privacy-list">
              <div className="privacy-list-item">
                <div className="list-bullet"></div>
                <div>
                  <strong>No Personal Information:</strong> We do not collect,
                  store, or transmit any personal information such as your name,
                  email address, phone number, or contacts.
                </div>
              </div>
              <div className="privacy-list-item">
                <div className="list-bullet"></div>
                <div>
                  <strong>Local Storage Only:</strong> All WhatsApp statuses
                  saved through our app are stored locally on your device. We do
                  not upload or store them on any remote servers.
                </div>
              </div>
              <div className="privacy-list-item">
                <div className="list-bullet"></div>
                <div>
                  <strong>No Account Required:</strong> The app does not require
                  you to create an account or log in, ensuring your privacy is
                  maintained.
                </div>
              </div>
              <div className="privacy-list-item">
                <div className="list-bullet"></div>
                <div>
                  <strong>Storage Permissions:</strong> The app requires storage
                  permissions to save statuses to your device's gallery. This is
                  the only permission we request.
                </div>
              </div>
            </div>
          </div>

          {/* How We Use Information */}
          <div className="privacy-section">
            <div className="section-icon-wrapper">
              <Eye className="section-icon" size={24} />
            </div>
            <h2 className="privacy-heading">How We Use Your Information</h2>
            <p className="privacy-text">
              Since Status Saver operates entirely on your device without
              collecting personal data:
            </p>
            <div className="privacy-list">
              <div className="privacy-list-item">
                <div className="list-bullet"></div>
                <div>
                  <strong>App Functionality:</strong> Storage permissions are
                  used solely to save WhatsApp statuses to your device's local
                  storage.
                </div>
              </div>
              <div className="privacy-list-item">
                <div className="list-bullet"></div>
                <div>
                  <strong>No Data Sharing:</strong> We do not share, sell, or
                  distribute any data to third parties.
                </div>
              </div>
              <div className="privacy-list-item">
                <div className="list-bullet"></div>
                <div>
                  <strong>No Analytics:</strong> We do not use analytics or
                  tracking tools that collect user behavior data.
                </div>
              </div>
            </div>
          </div>

          {/* Data Security */}
          <div className="privacy-section">
            <div className="section-icon-wrapper">
              <Lock className="section-icon" size={24} />
            </div>
            <h2 className="privacy-heading">Data Security</h2>
            <p className="privacy-text">
              Your data security is important to us. Since all data is stored
              locally on your device:
            </p>
            <div className="privacy-list">
              <div className="privacy-list-item">
                <div className="list-bullet"></div>
                <div>
                  <strong>Device Security:</strong> The security of saved
                  statuses depends on your device's security measures (screen
                  lock, encryption, etc.).
                </div>
              </div>
              <div className="privacy-list-item">
                <div className="list-bullet"></div>
                <div>
                  <strong>No Server Storage:</strong> Since we don't store data
                  on servers, there's no risk of server breaches or unauthorized
                  access to your saved statuses.
                </div>
              </div>
              <div className="privacy-list-item">
                <div className="list-bullet"></div>
                <div>
                  <strong>Open Source:</strong> Our app is open source
                  (GPL-3.0), allowing security experts to review our code for
                  transparency.
                </div>
              </div>
            </div>
          </div>

          {/* Third-Party Services */}
          <div className="privacy-section">
            <div className="section-icon-wrapper">
              <Shield className="section-icon" size={24} />
            </div>
            <h2 className="privacy-heading">Third-Party Services</h2>
            <p className="privacy-text">
              Status Saver does not integrate with third-party services for data
              collection or analytics. The app operates independently on your
              device.
            </p>
            <p className="privacy-text">
              <strong>Note:</strong> Status Saver is not affiliated with,
              endorsed by, or connected to WhatsApp Inc. or Meta Platforms, Inc.
              WhatsApp is a registered trademark of WhatsApp LLC.
            </p>
          </div>

          {/* Your Rights */}
          <div className="privacy-section">
            <div className="section-icon-wrapper">
              <FileText className="section-icon" size={24} />
            </div>
            <h2 className="privacy-heading">Your Rights & Responsibilities</h2>
            <p className="privacy-text">
              When using Status Saver, you have full control over your data:
            </p>
            <div className="privacy-list">
              <div className="privacy-list-item">
                <div className="list-bullet"></div>
                <div>
                  <strong>Delete Anytime:</strong> You can delete any saved
                  status from your device's gallery at any time.
                </div>
              </div>
              <div className="privacy-list-item">
                <div className="list-bullet"></div>
                <div>
                  <strong>Respect Others' Privacy:</strong> Please obtain
                  permission before saving or sharing someone else's WhatsApp
                  status. Respect intellectual property and privacy rights.
                </div>
              </div>
              <div className="privacy-list-item">
                <div className="list-bullet"></div>
                <div>
                  <strong>Uninstall:</strong> Uninstalling the app will remove
                  the application but not the statuses already saved to your
                  gallery.
                </div>
              </div>
            </div>
          </div>

          {/* Children's Privacy */}
          <div className="privacy-section">
            <div className="section-icon-wrapper">
              <Shield className="section-icon" size={24} />
            </div>
            <h2 className="privacy-heading">Children's Privacy</h2>
            <p className="privacy-text">
              Status Saver is intended for users who are at least 13 years old.
              We do not knowingly collect personal information from children
              under 13. If you are under 13, please do not use this app.
            </p>
          </div>

          {/* Changes to Privacy Policy */}
          <div className="privacy-section">
            <div className="section-icon-wrapper">
              <FileText className="section-icon" size={24} />
            </div>
            <h2 className="privacy-heading">Changes to This Privacy Policy</h2>
            <p className="privacy-text">
              We may update this privacy policy from time to time. Any changes
              will be posted on this page with an updated "Last Updated" date.
              We encourage you to review this policy periodically.
            </p>
          </div>

          {/* Contact Us */}
          <div className="privacy-section privacy-contact">
            <div className="section-icon-wrapper">
              <Mail className="section-icon" size={24} />
            </div>
            <h2 className="privacy-heading">Contact Us</h2>
            <p className="privacy-text">
              If you have any questions or concerns about this privacy policy or
              our practices, please feel free to:
            </p>
            <div className="contact-options">
              <a
                href={appData.links.github}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-link"
              >
                <Button className="btn-primary">
                  Visit Our GitHub Repository
                </Button>
              </a>
            </div>
            <p className="privacy-text privacy-note">
              This app is open source and licensed under GPL-3.0. You can review
              our code, report issues, or contribute to the project on GitHub.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="privacy-footer">
        <div className="privacy-footer-content">
          <p className="privacy-footer-text">
            &copy; 2025 Status Saver. All rights reserved. | Licensed under
            GPL-3.0
          </p>
          <Button
            onClick={() => navigate("/")}
            className="btn-secondary btn-small"
          >
            <ArrowLeft className="btn-icon" />
            Home
          </Button>
        </div>
      </footer>
    </div>
  );
};
